<script setup lang="ts">
import RemoveAttendeeFlowModal from "~/components/Pages/training-plan/modals/RemoveAttendeeFlowModal.vue";
import type { RemoveBookingDtoRequestActionEnum } from "@simbelapp/order-sdk";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { storeToRefs } from "pinia";
import { SessionItemTypeEnum } from "~/utils/enums/session-items.enums";
import { usePagination } from "~/composables/paginations/usePagination";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { useSessionDetailsAttendeesStore } from "~/store/sessions/session-details-attendees-store";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { useLearningPathAttendeeStore } from "~/store/learning-path/learning-path-attendee.store";

const useSessions = useSessionsStore();
const useSessionsDetails = useSessionDetailsStore();
const useSessionsDetailsAttendees = useSessionDetailsAttendeesStore();
const $router = useRouter();
const usePaginate = usePagination();
const emit = defineEmits(["hide"]);
const router = useRouter();
const routeName: string = router.currentRoute.value.name as string;
const learningPathAttendeesStore = useLearningPathAttendeeStore();

const { trainingPlanSessionIndividual } = storeToRefs(useSessions);

// Determine if a request is linked
const hasLinkedRequest = computed(() => !!trainingPlanSessionIndividual.value?.requestId);

// For single attendee removal, count is 1 if linked
const linkedAttendeesCount = computed(() => (hasLinkedRequest.value ? 1 : 0));

// Initialize store data on mount
onMounted(() => {
  const bookingId = trainingPlanSessionIndividual.value?.bookingId || "";
  useSessionsDetailsAttendees.removeAttendee.bookingId = bookingId;
  useSessionsDetailsAttendees.removeAttendee.isLinkedToARequest = hasLinkedRequest.value;
});

const deleteConsequences = ["Les éventuels documents associés seront supprimés"];

const notifyConsequences = [
  "Le prestataire sera notifié si vous avez renseigné un contact prestataire sur la session.",
];

function hideModal() {
  useSessionsDetailsAttendees.closeRemoveAttendeeFlow();
  emit("hide");
}

function onConfirmRequest(action: RemoveBookingDtoRequestActionEnum, comment?: string) {
  useSessionsDetailsAttendees.removeAttendee.requestAction = action;
  useSessionsDetailsAttendees.removeAttendee.requestComment = comment || null;
}

async function executeRemoval(doNotNotify: boolean) {
  useSessionsDetailsAttendees.removeAttendee.doNotNotify = doNotNotify;
  await useSessionsDetailsAttendees.executeRemoveAttendee();
  await useSessions.cancelTrainingPlanIndividualSession();

  if (useSessionsDetails.sessionViewModal) {
    await refreshSessionDetailsPage();
  } else {
    await usePaginate.fetchData();
  }

  useSessionsDetailsAttendees.closeRemoveAttendeeFlow();
  emit("hide");
  useSessions.showSessionModal = false;

  if (routeName.includes("learning-path")) {
    const route = useRoute();
    const { attendee_id, learning_path_id } = route.query;
    const paginate = usePagination(PaginationKeysEnum.LEARNING_PATH_ATTENDEE_JOURNEY_PAGINATION);
    const paginateOptions = paginate.getFindOptions(paginate.paginateOptions.value);
    await learningPathAttendeesStore.fetchLearningPathAttendeeJourneyFullDatas(
      attendee_id as string,
      learning_path_id as string,
      paginateOptions,
    );
  }
}

async function refreshSessionDetailsPage() {
  const isIndividualSession: boolean = trainingPlanSessionIndividual.value?.type === SessionItemTypeEnum.INDIVIDUAL;

  // The backend serialises `meta.count` as a string and the SDK passes it through, so a strict
  // `=== 1` never matched and this redirect was dead.
  const nbAttendees: number = Number(useSessionsDetails.sessionDetailsAttendeesBasic?.meta?.count ?? 0);

  // Individual sessions is not available if all it's attendees are canceled
  if (nbAttendees === 1 && isIndividualSession) {
    await usePaginate.fetchData();
    $router.push({ path: "/training-plan" });
  } else {
    await useSessionsDetails.fetchSessionDetails(
      useSessionsDetails.sessionDetails?.sessionId!,
      useSessionsDetails.sessionDetails?.itemType!,
    );
    await useSessionsDetails.refreshCurrentTabData();
  }
}

const loadCancelNotification = ref(false);
const loadConfirmNotification = ref(false);

async function cancelNotification() {
  loadConfirmNotification.value = true;
  await executeRemoval(true);
  loadConfirmNotification.value = false;
}

async function confirmNotification() {
  loadCancelNotification.value = true;
  await executeRemoval(false);
  loadCancelNotification.value = false;
}
</script>

<template>
  <RemoveAttendeeFlowModal
    :has-linked-request="hasLinkedRequest"
    :linked-attendees-count="linkedAttendeesCount"
    :show-notify-step="true"
    :delete-consequences="deleteConsequences"
    :notify-consequences="notifyConsequences"
    :loading-cancel-notify="loadCancelNotification"
    :loading-confirm-notify="loadConfirmNotification"
    @hide="hideModal"
    @confirm-request="onConfirmRequest"
    @cancel-notify="cancelNotification"
    @confirm-notify="confirmNotification"
  />
</template>

<style scoped lang="scss"></style>
